import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Empty } from 'components/Empty';
import { useNftFormattedTotalPrice } from 'hooks/nft/useNftFormattedTotalPrice';
import { useIsTerminalMode } from 'hooks/useIsTerminalMode';
import { NftPriceSummarySkeleton } from './Skeleton';
import * as Styled from './styles';
import { useAppSelector } from 'appRedux/hooks';
import {
  Configs,
  IS_FTNFT_AM_MODE,
  IS_KIOSK_MODE,
  IS_KIOSK_VENICE_MODE
} from 'constants/app/configs';

type NftPriceSummaryProps = {
  className?: string;
  useCurrentNft?: boolean;
  isMintConfirmed?: boolean;
};

export const NftPriceSummary: FC<NftPriceSummaryProps> = ({
  className,
  useCurrentNft = false,
  isMintConfirmed = false
}) => {
  const { t } = useTranslation();

  const { current: nftData } = useAppSelector(({ nfts }) => nfts);
  const { info: cartData, loading: cartLoading } = useAppSelector(
    ({ cart }) => cart
  );

  const {
    nftFormattedTotalPrice,
    transactionFee,
    transactionFeeLoading,
    transactionFeeLoaded,
    currency,
    VAT
  } = useNftFormattedTotalPrice(nftData.data, 'mint', isMintConfirmed);

  /*
   * TODO: Formatted prices, shown below, should be concatenated client-side
   * by using plain price and backed provided currency
   */
  const isLazyMint = !!(Configs.CART_ENABLED && !useCurrentNft
    ? cartData?.mint_amount
    : nftData.data?.lazy_mint);
  const price =
    Configs.CART_ENABLED && !useCurrentNft
      ? cartData?.formatted_sub_total
      : nftData.data?.formatted_price;
  const mintPrice =
    Configs.CART_ENABLED && !useCurrentNft
      ? cartData?.formatted_mint_amount
      : transactionFee?.data.data.formatted_price;
  const totalPrice =
    Configs.CART_ENABLED && !useCurrentNft
      ? cartData?.formatted_grand_total
      : nftFormattedTotalPrice;
  const VATPrice =
    Configs.CART_ENABLED && !useCurrentNft
      ? cartData?.formatted_tax_total
      : `${VAT} ${currency}`;
  const loading =
    Configs.CART_ENABLED && !useCurrentNft
      ? cartLoading
      : nftData.loading || transactionFeeLoading;
  const empty =
    Configs.CART_ENABLED && !useCurrentNft && !isMintConfirmed
      ? !nftData.data || !transactionFeeLoaded
      : !nftData.data;

  const { isTerminalMode } = useIsTerminalMode();

  if (loading) {
    return <NftPriceSummarySkeleton className={className} />;
  }

  if (empty) {
    return <Empty />;
  }

  return (
    <Styled.NftPriceSummaryTable
      className={className}
      $isLarge={isTerminalMode}
    >
      {!isMintConfirmed && (
        <div>
          <div>{t('generic.price')}</div>
          <div>{price}</div>
        </div>
      )}
      {!IS_FTNFT_AM_MODE && isLazyMint && !isMintConfirmed && (
        <div>
          <div>{t('nft.mintFee')}</div>
          <div>{mintPrice}</div>
        </div>
      )}
      {(IS_KIOSK_MODE || IS_KIOSK_VENICE_MODE) && (
        <div>
          <div>{`${t('cart.vat')}`}</div>
          <div>{VATPrice}</div>
        </div>
      )}
      {!IS_FTNFT_AM_MODE && (
        <div>
          <div>
            {isMintConfirmed ? t('generic.price') : t('nft.totalPrice')}
          </div>
          <div>{totalPrice}</div>
        </div>
      )}
    </Styled.NftPriceSummaryTable>
  );
};
